"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { FileSearch, Pencil } from "lucide-react";
import type { ResumeDocument } from "@/types/cv";
import { withCvBuilderAnalyticsContext } from "@/components/cv-builder/cv-builder-analytics";
import { PdfPreview } from "@/components/cv-builder/pdf-preview";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

interface BuilderResultProps {
  resume: ResumeDocument;
  analyticsContext?: UmamiEventData;
  locale: string;
  onEdit: () => void;
  canDownload?: boolean;
  onLockedDownload?: () => void;
  isPolishing?: boolean;
  polishError?: string | null;
}

export function BuilderResult({
  resume,
  analyticsContext,
  locale,
  onEdit,
  canDownload = true,
  onLockedDownload,
  isPolishing = false,
  polishError,
}: BuilderResultProps) {
  const trackedViewRef = useRef(false);

  useEffect(() => {
    if (trackedViewRef.current) return;

    trackedViewRef.current = true;
    trackEvent(
      "builder_result_view",
      withCvBuilderAnalyticsContext(analyticsContext, {
        source: "builder_result",
        can_download: canDownload,
      }),
    );
  }, [analyticsContext, canDownload]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          CV kamu sudah jadi 🎉
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          {canDownload
            ? "CV kamu rapi dan ATS-friendly. Cek pratinjaunya di bawah, lalu download PDF-nya."
            : "CV kamu rapi dan ATS-friendly. Cek pratinjaunya di bawah. Supaya hasil ini tidak hilang, simpan dulu lalu download PDF-nya."}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="mt-2 text-muted-foreground"
        >
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit CV
        </Button>
      </div>

      <PdfPreview
        resume={resume}
        analyticsContext={analyticsContext}
        canDownload={canDownload}
        onLockedDownload={onLockedDownload}
      />

      {polishError ? (
        <p className="text-center text-xs text-destructive">{polishError}</p>
      ) : null}

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <FileSearch className="h-5 w-5 text-foreground" />
          <p className="font-semibold">Cek skor ATS CV ini</p>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Lihat seberapa besar peluang CV kamu lolos screening ATS, plus apa
          yang perlu dibenerin. Gratis.
        </p>
        <Button
          asChild
          variant="outline"
          className="mt-4 w-full"
          onClick={() =>
            trackEvent(
              "builder_review_click",
              withCvBuilderAnalyticsContext(analyticsContext, {
                source: "builder_result",
              }),
            )
          }
        >
          <Link href={`/${locale}`}>Upload untuk cek skor ATS</Link>
        </Button>
      </div>
    </div>
  );
}
